import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, X, Compass } from 'lucide-react';
import { useTheme } from '../../App';
import { useNavigate } from 'react-router-dom';

export default function ServiceUnavailablePopup() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const navigate = useNavigate();
  const [popup, setPopup] = useState(null); // { message, service, open_time, close_time }

  useEffect(() => {
    function handler(e) {
      setPopup(e.detail);
    }
    window.addEventListener('service-unavailable', handler);
    return () => window.removeEventListener('service-unavailable', handler);
  }, []);

  if (!popup) return null;

  const serviceLabel = popup.service === 'recharge'
    ? '💳 Recharge'
    : popup.service === 'withdrawal'
    ? '🏦 Withdrawal'
    : '⚙️ This service';

  const timeInfo = popup.open_time && popup.close_time
    ? `Available: ${popup.open_time} – ${popup.close_time} IST`
    : null;

  return (
    <AnimatePresence>
      {popup && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '20px',
          }}
          onClick={() => setPopup(null)}>

          <motion.div
            initial={{ scale: 0.85, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 10 }}
            transition={{ type: 'spring', damping: 22, stiffness: 300 }}
            onClick={e => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: 360,
              background: isDark
                ? 'linear-gradient(160deg,#0f0f23,#0a0a18)'
                : '#fff',
              borderRadius: 24,
              border: `1px solid ${isDark ? 'rgba(20,184,166,0.25)' : 'rgba(20,184,166,0.18)'}`,
              overflow: 'hidden',
              boxShadow: '0 24px 64px rgba(0,0,0,0.45)',
            }}>

            {/* Gradient top bar */}
            <div style={{ height: 4, background: 'linear-gradient(90deg,#14b8a6,#0d9488,#ec4899)' }} />

            <div style={{ padding: '28px 24px 24px' }}>

              {/* Icon + close */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
                <div style={{
                  width: 60, height: 60, borderRadius: 18,
                  background: isDark ? 'rgba(20,184,166,0.12)' : 'rgba(20,184,166,0.08)',
                  border: `1px solid ${isDark ? 'rgba(20,184,166,0.25)' : 'rgba(20,184,166,0.18)'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Clock size={26} color="#2dd4bf" />
                </div>
                <button onClick={() => setPopup(null)}
                  style={{ width: 30, height: 30, borderRadius: 8, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', background: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)' }}>
                  <X size={15} color={isDark ? '#64748b' : '#9ca3af'} />
                </button>
              </div>

              {/* Text */}
              <h2 style={{ color: isDark ? '#f1f5f9' : '#1e1b4b', fontSize: 18, fontWeight: 800, margin: '0 0 8px' }}>
                {serviceLabel} is Closed
              </h2>
              <p style={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: 13, lineHeight: 1.6, margin: '0 0 16px' }}>
                {popup.message || `${serviceLabel} is not available right now. Please try again later.`}
              </p>

              {/* Time window pill */}
              {timeInfo && (
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: 7,
                  background: isDark ? 'rgba(20,184,166,0.1)' : 'rgba(20,184,166,0.07)',
                  border: `1px solid ${isDark ? 'rgba(20,184,166,0.22)' : 'rgba(20,184,166,0.15)'}`,
                  borderRadius: 10, padding: '8px 14px', marginBottom: 20,
                }}>
                  <Clock size={13} color="#2dd4bf" />
                  <span style={{ color: '#2dd4bf', fontSize: 12, fontWeight: 600 }}>{timeInfo}</span>
                </div>
              )}

              {/* What you can do */}
              <div style={{
                background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(20,184,166,0.04)',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(20,184,166,0.1)'}`,
                borderRadius: 14, padding: '14px 16px', marginBottom: 20,
              }}>
                <p style={{ color: isDark ? '#64748b' : '#9ca3af', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', margin: '0 0 10px' }}>
                  Meanwhile you can
                </p>
                {[
                  { icon: '📈', text: 'Check your investments & claim profits' },
                  { icon: '👥', text: 'Refer friends and earn commission' },
                  { icon: '🎯', text: 'Complete daily tasks for rewards' },
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', gap: 10, marginBottom: i < 2 ? 8 : 0, alignItems: 'center' }}>
                    <span style={{ fontSize: 15, flexShrink: 0 }}>{item.icon}</span>
                    <span style={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: 12, lineHeight: 1.4 }}>{item.text}</span>
                  </div>
                ))}
              </div>

              {/* Buttons */}
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => setPopup(null)}
                  style={{ flex: 1, padding: '12px', borderRadius: 14, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(20,184,166,0.15)'}`, background: 'transparent', color: isDark ? '#94a3b8' : '#6b7280' }}>
                  Close
                </button>
                <button
                  onClick={() => { setPopup(null); navigate('/dashboard'); }}
                  style={{ flex: 1.5, padding: '12px', borderRadius: 14, fontSize: 13, fontWeight: 700, cursor: 'pointer', border: 'none', background: 'linear-gradient(135deg,#14b8a6,#0d9488)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, boxShadow: '0 4px 16px rgba(20,184,166,0.35)' }}>
                  <Compass size={14} /> Explore App
                </button>
              </div>

            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
